import { Fragment, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { BrandFavicon } from '../brand/BrandFavicon';
import { EngineLogo, ENGINE_LABEL } from '../../pages/dashboard/EngineLogo';
import { Flag } from '../../pages/dashboard/Flag';
import { languageFlag } from '../../pages/dashboard/geoFlags';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../contexts/BrandContext';
import { useAuth } from '../../contexts/AuthContext';
import { api, ApiError } from '../../services/api';
import CadenceEditor, { AXIS_LABEL_KEYS } from '../brand/CadenceEditor';
import { SCOPE_LABELS } from '../brand/strategy-sections';
import {
  describeCadence,
  isBrandAxisAuditType,
  isCadenceConfigurable,
  resolveEntitlement,
  resolveLimits,
  SCHEDULABLE_AUDIT_TYPES,
  type Brand,
  type Cadence,
  type UsageMetric,
  type BrandUsage,
  type SchedulableAuditType,
} from '@traaker/shared';

interface UsageResponse {
  tier: string;
  metrics: UsageMetric[];
  cadenceConfigurable: boolean;
  perBrand: BrandUsage[];
}

export default function BrandsSettings() {
  const { t } = useTranslation();
  const { brands, refreshBrands, setActiveBrand, loading: brandsLoading } = useBrand();
  const { tenant, user } = useAuth();
  const navigate = useNavigate();
  const [brandDialog, setBrandDialog] = useState<{ open: boolean; brand?: Brand }>({ open: false });
  const [message, setMessage] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; brand?: Brand }>({ open: false });
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Brands whose DELETE failed part-way: hidden from `GET /brands` (row kept with
  // `deletingAt`), listed here so the user can re-run the idempotent cascade.
  const [pendingDeletion, setPendingDeletion] = useState<Brand[]>([]);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  // undefined = loading, null = failed.
  const [usage, setUsage] = useState<UsageResponse | null | undefined>(undefined);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const canManage =
    user?.role === 'owner' || user?.role === 'admin' || user?.role === 'member';
  const tier = tenant?.tier || 'basic';
  // Authoritative brand limit: tier default + packs + override, ceiling-clamped.
  // resolveEntitlement is the single source of truth (no stale local map).
  const brandLimit = resolveEntitlement('brands', tier, {
    packs: tenant?.settings?.activePacks?.brands,
    override: tenant?.settings?.quotaOverrides?.brands,
  }).limit;
  const canAddBrand = brandLimit === -1 || brands.length < brandLimit;
  const isAgency = tier === 'agency';
  // Same gate as the Sidebar's Conversations item: a 0 `conversation_personas` limit hides it.
  const showConversations =
    resolveLimits(tier, tenant?.settings?.quotaOverrides).conversation_personas !== 0;

  // Add-brand click: route to onboarding when within quota, otherwise show the
  // upgrade popup (which routes to the plans page).
  const handleAddBrandClick = () => {
    if (canAddBrand) navigate('/onboarding/brand/new');
    else setUpgradeOpen(true);
  };

  // Tenant-wide quota usage summary — ONE call on mount (4 DDB reads server-side,
  // no per-brand fan-out). Refreshed when the brand list changes (add/delete).
  useEffect(() => {
    let cancelled = false;
    api
      .get<UsageResponse>('/metering/usage')
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch(() => { if (!cancelled) setUsage(null); });
    return () => { cancelled = true; };
  }, [brands.length]);

  const refreshPendingDeletion = () =>
    api
      .get<Brand[]>('/brands/pending-deletion')
      .then(setPendingDeletion)
      .catch(() => setPendingDeletion([]));

  useEffect(() => {
    refreshPendingDeletion();
  }, [brands.length]);

  const isPartialDelete = (err: unknown) =>
    err instanceof ApiError && err.body?.code === 'BRAND_DELETE_PARTIAL';

  const handleRetryDelete = async (brand: Brand) => {
    setRetryingId(brand.brandId);
    try {
      await api.delete(`/brands/${brand.brandId}`);
      setMessage(t('brandsSettings.toast.brandDeleted', { name: brand.name }));
    } catch (err) {
      setMessage(
        isPartialDelete(err)
          ? t('brandsSettings.toast.deletePartial', { name: brand.name })
          : t('brandsSettings.toast.deleteError'),
      );
    } finally {
      setRetryingId(null);
      await refreshPendingDeletion();
    }
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteDialog({ open: false });
    setDeleteConfirmation('');
  };

  const handleDeleteBrand = async () => {
    if (!deleteDialog.brand) return;
    const target = deleteDialog.brand;
    if (deleteConfirmation.trim().toLowerCase() !== target.name.trim().toLowerCase()) return;
    setDeleting(true);
    try {
      await api.delete(`/brands/${target.brandId}`);
      await refreshBrands();
      setDeleteDialog({ open: false });
      setDeleteConfirmation('');
      setMessage(t('brandsSettings.toast.brandDeleted', { name: target.name }));
    } catch (err) {
      if (isPartialDelete(err)) {
        // The brand is now hidden from the list; it reappears in the
        // « Suppression incomplète » block below with a retry button.
        await refreshBrands();
        await refreshPendingDeletion();
        setDeleteDialog({ open: false });
        setDeleteConfirmation('');
        setMessage(t('brandsSettings.toast.deletePartial', { name: target.name }));
      } else {
        setMessage(t('brandsSettings.toast.deleteError'));
      }
    } finally {
      setDeleting(false);
    }
  };

  const goManageLocations = (brand: Brand) => {
    setActiveBrand(brand);
    navigate('/strategy');
  };

  return (
    <Box>
      {message && (
        <Alert severity="info" onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {pendingDeletion.map((brand) => (
        <Alert
          key={brand.brandId}
          severity="warning"
          sx={{ mb: 2 }}
          action={
            canManage && (
              <Button
                color="inherit"
                size="small"
                disabled={retryingId === brand.brandId}
                onClick={() => handleRetryDelete(brand)}
              >
                {t('brandsSettings.pendingDeletion.retry')}
              </Button>
            )
          }
        >
          <strong>{t('brandsSettings.pendingDeletion.title')}</strong>
          {' · '}
          {t('brandsSettings.pendingDeletion.body', { name: brand.name })}
        </Alert>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }} data-tour="brands-manage">
        <Typography variant="h6" fontWeight={600}>
          {t('brandsSettings.title')}
        </Typography>
        {canManage && (
          <Tooltip title={canAddBrand ? '' : t('brandsSettings.brandCapReached', { limit: brandLimit })}>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
              onClick={handleAddBrandClick}
            >
              {t('brandsSettings.addBrand')}
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Layout keyed on the tenant tier (known at mount), never on `usage`: keying it on the
          usage response rendered the non-agency layout for ~1 s before swapping. */}
      {!isAgency && (usage ? <UsageSummary usage={usage} /> : usage === undefined && brands.length > 0 && (
        <Skeleton variant="rounded" height={64} sx={{ mb: 2 }} />
      ))}

      {brands.length > 0 && (
        <BrandsTable
          brands={brands}
          usage={usage}
          tier={tier}
          showConversations={showConversations}
          canManage={canManage}
          onOpen={(brand) => { setActiveBrand(brand); navigate('/'); }}
          onManageLocations={goManageLocations}
          onEdit={(brand) => setBrandDialog({ open: true, brand })}
          onDelete={(brand) => {
            setDeleteConfirmation('');
            setDeleteDialog({ open: true, brand });
          }}
        />
      )}

      {brands.length === 0 && !brandsLoading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('brandsSettings.noBrandConfigured')}
            </Typography>
          </CardContent>
        </Card>
      )}

      <BrandDialog
        {...brandDialog}
        tier={tier}
        onClose={() => setBrandDialog({ open: false })}
        onSaved={refreshBrands}
        onCreated={(brandId) => navigate(`/onboarding/brand/${brandId}`)}
      />
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('brandsSettings.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('brandsSettings.delete.warning', { name: deleteDialog.brand?.name })}
            <br />
            <br />
            {t('brandsSettings.delete.confirmPrompt', { name: deleteDialog.brand?.name })}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t('brandsSettings.delete.nameLabel')}
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            disabled={deleting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            {t('brandsSettings.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteBrand}
            disabled={
              deleting ||
              !deleteDialog.brand ||
              deleteConfirmation.trim().toLowerCase() !==
                (deleteDialog.brand?.name || '').trim().toLowerCase()
            }
          >
            {deleting ? t('brandsSettings.delete.deleting') : t('brandsSettings.delete.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <UpgradeBrandDialog
        open={upgradeOpen}
        limit={brandLimit}
        onClose={() => setUpgradeOpen(false)}
        onUpgrade={() => {
          setUpgradeOpen(false);
          navigate('/settings?tab=billing');
        }}
      />
    </Box>
  );
}

// ── Brand-quota reached: upgrade prompt ──

function UpgradeBrandDialog({
  open,
  limit,
  onClose,
  onUpgrade,
}: {
  open: boolean;
  limit: number;
  onClose: () => void;
  onUpgrade: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Box
          sx={{
            width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
          }}
        >
          <WorkspacePremiumIcon sx={{ fontSize: 30, color: 'primary.main' }} />
        </Box>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('brandsSettings.upgrade.title')}
        </Typography>
        <DialogContentText sx={{ fontSize: 14 }}>
          {t('brandsSettings.upgrade.body', { count: limit })}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
        <Button onClick={onClose}>{t('brandsSettings.close')}</Button>
        <Button variant="contained" startIcon={<WorkspacePremiumIcon />} onClick={onUpgrade}>
          {t('brandsSettings.upgrade.viewPlans')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Usage summary (tenant-wide gauges) ──

// Display order — most commercially relevant first.
const USAGE_ORDER = ['prompts', 'brands', 'zones', 'languages', 'competitors', 'users', 'tasks'];

function UsageSummary({ usage }: { usage: UsageResponse }) {
  const { t } = useTranslation();
  const byMetric = new Map((usage.metrics ?? []).map((m) => [m.metric, m]));
  const ordered = USAGE_ORDER.map((k) => byMetric.get(k)).filter(Boolean) as UsageMetric[];

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
          {t('brandsSettings.usageShared', { tier: usage.tier })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
          {ordered.map((m) => {
            const unlimited = m.limit === -1;
            const over = !unlimited && m.used >= m.limit;
            const near = !unlimited && !over && m.used >= m.limit * 0.8;
            return (
              <Chip
                key={m.metric}
                size="small"
                variant="outlined"
                color={over ? 'error' : near ? 'warning' : 'default'}
                label={`${m.label.fr} · ${m.used}${unlimited ? '' : `/${m.limit}`}`}
              />
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Per-brand cadence chip strip (read-only) ──

/**
 * The brand's cadences as shown here: the non-prompt axes, then ONE prompt cadence and ONE
 * conversation cadence. The per-axis `brand_*` entries are legacy overrides, not what runs.
 */
function brandCadences(bu: BrandUsage): { key: string; labelKey: string; cadence: Cadence }[] {
  const rows: { key: string; labelKey: string; cadence: Cadence }[] = SCHEDULABLE_AUDIT_TYPES
    .filter((axis) => !isBrandAxisAuditType(axis) && bu.cadence?.[axis])
    .map((axis) => ({ key: axis, labelKey: AXIS_LABEL_KEYS[axis as SchedulableAuditType], cadence: bu.cadence[axis] }));
  if (bu.promptCadence) rows.push({ key: 'prompts', labelKey: 'cadenceEditor.axis.prompts', cadence: bu.promptCadence });
  if (bu.conversationCadence) {
    rows.push({ key: 'conversations', labelKey: 'cadenceEditor.axis.conversations', cadence: bu.conversationCadence });
  }
  return rows;
}

function CadenceStrip({ bu }: { bu?: BrandUsage }) {
  const { t } = useTranslation();
  if (!bu?.cadence) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  return (
    <>
      {brandCadences(bu).map(({ key, labelKey, cadence: c }) => {
        const off = c === 'off';
        return (
          <Chip
            key={key}
            size="small"
            variant="outlined"
            label={`${t(labelKey)} : ${off ? t('brandsSettings.disabled') : describeCadence(c)}`}
            sx={{ opacity: off ? 0.55 : 1 }}
          />
        );
      })}
    </>
  );
}

// ── Agency per-brand feature usage cards ──

// Default AI engines when a brand has no explicit `enginesEnabled` allowlist
// (mirrors the run-path default = primary engines, secondary opt-in only).
const DEFAULT_ENGINE_IDS = ['perplexity', 'openai', 'gemini', 'claude'];

// Subtle strip of activated-model logos for an agency brand card. Each logo
// shows its model name on hover. Reads the per-brand `enginesEnabled` allowlist,
// falling back to the primary roster when unset.
function ActivatedEngines({ brand }: { brand?: Brand }) {
  const saved = brand?.auditConfig?.enginesEnabled;
  const engines = saved && saved.length > 0 ? saved : DEFAULT_ENGINE_IDS;
  if (engines.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {engines.map((e) => (
        <Tooltip key={e} title={ENGINE_LABEL[e] || e} arrow>
          <Box sx={{ display: 'flex', opacity: 0.85, '&:hover': { opacity: 1 } }}>
            <EngineLogo engine={e} size={16} />
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

// ── Brand list (every tier) — one row per brand, sortable, searchable ──

type SortKey = 'name' | 'prompts' | 'conversations' | 'zones' | 'languages' | 'competitors';

const MAX_FLAGS = 3;
const SEARCH_MIN_BRANDS = 6;

/** Language flags, up to MAX_FLAGS then a « +N » chip; the tooltip lists every code. */
function LanguageFlags({ codes }: { codes: string[] }) {
  if (codes.length === 0) return <Typography variant="body2" color="text.disabled">—</Typography>;
  const shown = codes.slice(0, MAX_FLAGS);
  const rest = codes.length - shown.length;
  return (
    <Tooltip title={codes.map((c) => c.toUpperCase()).join(' · ')} arrow>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {shown.map((c) => (
          languageFlag(c)
            ? <Flag key={c} countryCode={languageFlag(c)} size={18} />
            : <Typography key={c} variant="caption" fontWeight={600}>{c.toUpperCase()}</Typography>
        ))}
        {rest > 0 && (
          <Chip label={`+${rest}`} size="small" variant="outlined" sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
        )}
      </Box>
    </Tooltip>
  );
}

/** Shortest cadence label: « 1 j » / « 2 sem » / « 1 mois », 'off' (no cadence = manual) → « — ». */
function shortCadence(c: Cadence, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (c === 'off') return '—';
  return t(`brandsSettings.table.cadenceShort.${c.unit}`, { count: c.interval });
}

/** Prompt + conversation cadence side by side, icon-labelled; the tooltip gives the full wording. */
function CadenceCell({ bu, showConversations }: { bu?: BrandUsage; showConversations: boolean }) {
  const { t } = useTranslation();
  const items = [
    { key: 'prompts', Icon: ChatBubbleOutlineIcon, labelKey: 'cadenceEditor.axis.prompts', cadence: bu?.promptCadence },
    ...(showConversations
      ? [{ key: 'conversations', Icon: RecordVoiceOverOutlinedIcon, labelKey: 'cadenceEditor.axis.conversations', cadence: bu?.conversationCadence }]
      : []),
  ].filter((i): i is typeof i & { cadence: Cadence } => i.cadence !== undefined);
  if (items.length === 0) return <Typography variant="body2" color="text.disabled">—</Typography>;
  return (
    <Tooltip
      arrow
      title={items.map((i) => `${t(i.labelKey)} : ${i.cadence === 'off' ? t('brandsSettings.table.cadenceOff') : describeCadence(i.cadence)}`).join(' · ')}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25, cursor: 'default' }}>
        {items.map(({ key, Icon, cadence }) => (
          <Box key={key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: cadence === 'off' ? 'text.disabled' : 'text.primary' }}>
            <Icon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="body2" component="span" color="inherit">{shortCadence(cadence, t)}</Typography>
          </Box>
        ))}
      </Box>
    </Tooltip>
  );
}

/** A count cell whose tooltip lists the items behind it. */
function CountCell({ count, items }: { count: number; items: string[] }) {
  if (count === 0) return <Typography variant="body2" color="text.disabled">0</Typography>;
  return (
    <Tooltip title={items.join(' · ')} arrow>
      <Typography variant="body2" component="span" sx={{ cursor: 'default' }}>{count}</Typography>
    </Tooltip>
  );
}

function BrandsTable({
  brands,
  usage,
  tier,
  showConversations,
  canManage,
  onOpen,
  onManageLocations,
  onEdit,
  onDelete,
}: {
  brands: Brand[];
  /** undefined = loading (usage-backed cells show a skeleton), null = failed (« — »). */
  usage: UsageResponse | null | undefined;
  tier: string;
  showConversations: boolean;
  canManage: boolean;
  onOpen: (brand: Brand) => void;
  onManageLocations: (brand: Brand) => void;
  onEdit: (brand: Brand) => void;
  onDelete: (brand: Brand) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [expanded, setExpanded] = useState<string | null>(null);
  const usageById = new Map((usage?.perBrand ?? []).map((b) => [b.brandId, b]));
  // Languages the brand's prompts + conversations measure; Brand Knowledge's target
  // languages only when the API predates `usedLanguages` (or the call failed).
  const languagesOf = (b: Brand): string[] =>
    usageById.get(b.brandId)?.usedLanguages ?? b.strategy?.targetLanguages ?? [];

  const valueOf = (b: Brand, key: SortKey): number | string => {
    const bu = usageById.get(b.brandId);
    switch (key) {
      case 'name': return b.name.toLowerCase();
      case 'prompts': return bu?.prompts ?? -1;
      case 'conversations': return bu?.conversations ?? -1;
      case 'zones': return b.locations?.length ?? 0;
      case 'languages': return languagesOf(b).length;
      case 'competitors': return b.competitors?.length ?? 0;
    }
  };

  const q = query.trim().toLowerCase();
  const rows = brands
    .filter((b) => !q || b.name.toLowerCase().includes(q) || (b.url ?? '').toLowerCase().includes(q))
    .sort((a, b) => {
      const va = valueOf(a, sort.key);
      const vb = valueOf(b, sort.key);
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });

  const header = (key: SortKey, label: string, align: 'left' | 'right' = 'right') => (
    <TableCell align={align} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key ? sort.dir : key === 'name' ? 'asc' : 'desc'}
        onClick={() => setSort((s) => ({
          key,
          // Numbers sort high → low first: the « which brand is biggest » question.
          dir: s.key === key ? (s.dir === 'asc' ? 'desc' : 'asc') : key === 'name' ? 'asc' : 'desc',
        }))}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const usageCell = (value: number | undefined) =>
    usage === undefined
      ? <Skeleton width={24} sx={{ ml: 'auto' }} />
      : value === undefined ? <Typography variant="body2" color="text.disabled">—</Typography>
        : <Typography variant="body2" color={value === 0 ? 'text.disabled' : undefined}>{value}</Typography>;

  const colCount = 8 + (showConversations ? 1 : 0) + (canManage ? 1 : 0);

  return (
    <Box>
      {brands.length >= SEARCH_MIN_BRANDS && (
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('brandsSettings.search')}
          sx={{ mb: 1.5, width: { xs: '100%', sm: 320 } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: 'action.hover', fontWeight: 600, color: 'text.secondary' } }}>
              <TableCell padding="checkbox" />
              {header('name', t('brandsSettings.table.brand'), 'left')}
              {header('prompts', t('brandsSettings.feature.prompts'))}
              {showConversations && header('conversations', t('brandsSettings.table.conversations'))}
              {header('zones', t('brandsSettings.feature.zones'))}
              {header('languages', t('brandsSettings.feature.languages'), 'left')}
              {header('competitors', t('brandsSettings.feature.competitors'))}
              <TableCell>{t('brandsSettings.table.cadence')}</TableCell>
              <TableCell>{t('brandsSettings.table.models')}</TableCell>
              {canManage && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((brand) => {
              const bu = usageById.get(brand.brandId);
              const open = expanded === brand.brandId;
              const locations = brand.locations ?? [];
              const competitors = brand.competitors ?? [];
              return (
                <Fragment key={brand.brandId}>
                  <TableRow hover sx={{ '& > td': { borderBottom: open ? 'none' : undefined } }}>
                    <TableCell padding="checkbox">
                      <IconButton size="small" onClick={() => setExpanded(open ? null : brand.brandId)}>
                        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <BrandFavicon brandId={brand.brandId} size={28} />
                        <Box sx={{ minWidth: 0 }}>
                          <Link
                            component="button"
                            variant="body2"
                            fontWeight={600}
                            underline="hover"
                            onClick={() => onOpen(brand)}
                            sx={{ display: 'block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}
                          >
                            {brand.name}
                          </Link>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {brand.url?.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '') || t('brandsSettings.noWebsite')}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{usageCell(bu?.prompts)}</TableCell>
                    {showConversations && (
                      <TableCell align="right">
                        {usage === undefined || bu?.conversations === undefined ? usageCell(bu?.conversations) : (
                          <Tooltip title={t('brandsSettings.table.runsPerLaunch', { count: bu.conversationRunsPerLaunch ?? 0 })} arrow>
                            <Box component="span" sx={{ cursor: 'default' }}>
                              <Typography variant="body2" component="span" color={bu.conversations === 0 ? 'text.disabled' : undefined}>
                                {bu.conversations}
                              </Typography>
                              {bu.conversations > 0 && (
                                <Typography variant="caption" component="span" color="text.secondary">
                                  {' · '}{t('brandsSettings.table.runs', { count: bu.conversationRunsPerLaunch ?? 0 })}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <CountCell count={locations.length} items={locations.map((l) => l.label)} />
                    </TableCell>
                    <TableCell>
                      {usage === undefined ? <Skeleton width={56} /> : <LanguageFlags codes={languagesOf(brand)} />}
                    </TableCell>
                    <TableCell align="right">
                      <CountCell count={competitors.length} items={competitors} />
                    </TableCell>
                    <TableCell>
                      {usage === undefined ? <Skeleton width={72} /> : (
                        <CadenceCell bu={bu} showConversations={showConversations} />
                      )}
                    </TableCell>
                    <TableCell>
                      <ActivatedEngines brand={brand} />
                    </TableCell>
                    {canManage && (
                      <TableCell align="right" sx={{ pr: 1 }}>
                        <IconButton size="small" onClick={() => onEdit(brand)} aria-label={t('brandsSettings.edit')}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => onDelete(brand)} aria-label={t('brandsSettings.deleteAria')}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={colCount} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                      <Collapse in={open} unmountOnExit>
                        <BrandDetails
                          brand={brand}
                          bu={bu}
                          tier={tier}
                          canManage={canManage}
                          onManageLocations={() => onManageLocations(brand)}
                        />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('brandsSettings.noMatch', { query: query.trim() })}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/** Expanded row: zones, competitors and audit cadence of one brand. */
function BrandDetails({
  brand,
  bu,
  tier,
  canManage,
  onManageLocations,
}: {
  brand: Brand;
  bu?: BrandUsage;
  tier: string;
  canManage: boolean;
  onManageLocations: () => void;
}) {
  const { t } = useTranslation();
  const sectionTitle = (label: string) => (
    <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
      {label}
    </Typography>
  );
  return (
    <Box sx={{ py: 1.5, pl: 7, pr: 2, display: 'flex', flexDirection: 'column', gap: 1.5, whiteSpace: 'normal' }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {sectionTitle(t('brandsSettings.geoZones'))}
          {canManage && (
            <Button size="small" variant="text" onClick={onManageLocations} sx={{ textTransform: 'none', py: 0 }}>
              {t('brandsSettings.manage')} →
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75, alignItems: 'center' }}>
          {(brand.locations ?? []).map((loc) => (
            <Chip
              key={loc.locationId}
              label={`${loc.label}${loc.scope ? ` (${SCOPE_LABELS[loc.scope]})` : ''}`}
              size="small"
              variant="outlined"
            />
          ))}
          {(brand.locations?.length ?? 0) === 0 && (
            <Typography variant="body2" color="text.secondary">
              {canManage ? t('brandsSettings.noZoneAdmin') : t('brandsSettings.noZoneMember')}
            </Typography>
          )}
        </Box>
      </Box>

      {(brand.competitors?.length ?? 0) > 0 && (
        <Box>
          {sectionTitle(t('brandsSettings.competitors'))}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
            {(brand.competitors ?? []).map((c) => (
              <Chip key={c} label={c} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          {sectionTitle(t('brandsSettings.auditCadence'))}
          {tier !== 'agency' && (
            <Typography variant="caption" color="text.secondary">
              {isCadenceConfigurable(tier, 'website') && isCadenceConfigurable(tier, 'earned_media')
                ? t('brandsSettings.cadenceCustomizable')
                : t('brandsSettings.cadenceIncluded', { tier })}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
          <CadenceStrip bu={bu} />
        </Box>
      </Box>
    </Box>
  );
}

function BrandDialog({
  open,
  brand,
  tier,
  onClose,
  onSaved,
  onCreated,
}: {
  open: boolean;
  brand?: Brand;
  tier: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onCreated?: (brandId: string, brandName: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [includeUrls, setIncludeUrls] = useState('');
  const [excludeUrls, setExcludeUrls] = useState('');
  const [emWatchInclude, setEmWatchInclude] = useState('');
  const [emWatchExclude, setEmWatchExclude] = useState('');
  const [showAuditConfig, setShowAuditConfig] = useState(false);
  const [showEarnedMediaWatch, setShowEarnedMediaWatch] = useState(false);
  const [showCadence, setShowCadence] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(brand?.name || '');
      setUrl(brand?.url || '');
      setIncludeUrls((brand?.auditConfig?.includeUrls || []).join('\n'));
      setExcludeUrls((brand?.auditConfig?.excludeUrls || []).join('\n'));
      setEmWatchInclude(
        (brand?.auditConfig?.earnedMediaWatch?.include || []).join('\n'),
      );
      setEmWatchExclude(
        (brand?.auditConfig?.earnedMediaWatch?.exclude || []).join('\n'),
      );
      setShowAuditConfig(
        (brand?.auditConfig?.includeUrls?.length || 0) > 0 ||
        (brand?.auditConfig?.excludeUrls?.length || 0) > 0,
      );
      setShowEarnedMediaWatch(
        (brand?.auditConfig?.earnedMediaWatch?.include?.length || 0) > 0 ||
        (brand?.auditConfig?.earnedMediaWatch?.exclude?.length || 0) > 0,
      );
      setError(null);
    }
  }, [open, brand]);

  const handleSubmit = async () => {
    setError(null);
    const parseLines = (s: string) =>
      s.split('\n').map((l) => l.trim()).filter(Boolean);
    const includes = parseLines(includeUrls);
    const excludes = parseLines(excludeUrls);
    const watchInclude = parseLines(emWatchInclude);
    const watchExclude = parseLines(emWatchExclude);
    try {
      if (brand) {
        // Name + URL are read-only after creation (identity-defining) — only the
        // audit-config fields are editable here. They go through
        // PATCH /brands/:id/audit-config so they merge with cadenceOverride +
        // schedules instead of replacing the full auditConfig.
        await api.patch(`/brands/${brand.brandId}/audit-config`, {
          includeUrls: includes,
          excludeUrls: excludes,
          earnedMediaWatch: {
            include: watchInclude,
            exclude: watchExclude,
          },
        });
        await onSaved();
        onClose();
      } else {
        // Competitors are managed on the Concurrents page — NOT sent here.
        const created = await api.post<Brand>('/brands', { name, url: url || undefined });
        await onSaved();
        onClose();
        if (url && onCreated) {
          onCreated(created.brandId, created.name);
        }
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{brand ? t('brandsSettings.dialog.editTitle') : t('brandsSettings.dialog.addTitle')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {brand ? (
          <>
            {/* Name + URL are identity-defining (favicon, DomainProfile, all audit
                history is keyed to the domain) — read-only after creation. To
                change them, delete the brand and create a new one. */}
            <Box>
              <Typography variant="caption" color="text.secondary">{t('brandsSettings.dialog.nameLabel')}</Typography>
              <Typography variant="body1" fontWeight={600}>{brand.name}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">{t('brandsSettings.dialog.websiteLabel')}</Typography>
              <Typography variant="body1">{brand.url || t('brandsSettings.noWebsite')}</Typography>
            </Box>
            <Alert severity="info" sx={{ py: 0.5 }}>
              {t('brandsSettings.dialog.readOnlyNote')}
            </Alert>
          </>
        ) : (
          <>
            <TextField label={t('brandsSettings.dialog.nameLabel')} value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
            <TextField label={t('brandsSettings.dialog.websiteLabel')} value={url} onChange={(e) => setUrl(e.target.value)} fullWidth placeholder="https://example.com" required />
          </>
        )}

        {brand && (
          <>
            {/* Each collapsible section is wrapped in a single container so the
                header + Collapse form ONE flex child of the gap column — a bare
                Collapse as a direct flex-gap child overlaps its neighbour. */}
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box
                onClick={() => setShowAuditConfig(!showAuditConfig)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  cursor: 'pointer', userSelect: 'none',
                }}
              >
                {showAuditConfig ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('brandsSettings.dialog.advancedPageSelection')}
                </Typography>
              </Box>
              <Collapse in={showAuditConfig}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('brandsSettings.dialog.advancedPageHelp')}
                  </Typography>
                  <TextField
                    label={t('brandsSettings.dialog.alwaysInclude')}
                    value={includeUrls}
                    onChange={(e) => setIncludeUrls(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder="https://example.com/page-importante&#10;**/category/featured/*"
                    helperText={t('brandsSettings.dialog.alwaysIncludeHelp')}
                  />
                  <TextField
                    label={t('brandsSettings.dialog.alwaysExclude')}
                    value={excludeUrls}
                    onChange={(e) => setExcludeUrls(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder="**/drafts/*&#10;**/internal-*"
                    helperText={t('brandsSettings.dialog.alwaysExcludeHelp')}
                  />
                </Box>
              </Collapse>
            </Box>

            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box
                onClick={() => setShowEarnedMediaWatch(!showEarnedMediaWatch)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  cursor: 'pointer', userSelect: 'none',
                }}
              >
                {showEarnedMediaWatch ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('brandsSettings.dialog.earnedMediaWatch')}
                </Typography>
              </Box>
              <Collapse in={showEarnedMediaWatch}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('brandsSettings.dialog.earnedMediaWatchHelp')}
                  </Typography>
                  <TextField
                    label={t('brandsSettings.dialog.watchInclude')}
                    value={emWatchInclude}
                    onChange={(e) => setEmWatchInclude(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder="silicon.fr&#10;latribune.fr&#10;lesnumeriques.com"
                    helperText={t('brandsSettings.dialog.watchIncludeHelp')}
                  />
                  <TextField
                    label={t('brandsSettings.dialog.watchExclude')}
                    value={emWatchExclude}
                    onChange={(e) => setEmWatchExclude(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder="acme-toolworks.com&#10;acme.health"
                    helperText={t('brandsSettings.dialog.watchExcludeHelp')}
                  />
                </Box>
              </Collapse>
            </Box>

            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box
                onClick={() => setShowCadence(!showCadence)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  cursor: 'pointer', userSelect: 'none',
                }}
              >
                {showCadence ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('brandsSettings.auditCadence')}
                </Typography>
              </Box>
              <Collapse in={showCadence}>
                <Box sx={{ pt: 2 }}>
                  <CadenceEditor
                    brandId={brand.brandId}
                    tier={tier}
                    cadenceOverride={brand.auditConfig?.cadenceOverride}
                    onSaved={onSaved}
                  />
                </Box>
              </Collapse>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('brandsSettings.cancel')}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!brand && (!name || !url)}>
          {brand ? t('brandsSettings.save') : t('brandsSettings.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

